// packages/core/src/learning/school-modules/frugality.ts
// ─────────────────────────────────────────────────────────────────────────
// Lens Pack for "Frugality". surface: 'school', homeSchool: 'frugality'.
// module id 'school-frugality' matches the exhibit's nativeModuleId.
//
// emoji + accentColor are PLACEHOLDERS (accentColor from SchoolMeta color
// #2a9d8f) — flag for the presentation-tuning pass.
// tier/pillar: spending-control lens in the freedom-lifestyle-paths bucket →
// tier 'control', pillar 'cash-flow-control'.
// ─────────────────────────────────────────────────────────────────────────

import type { LearningModule } from '../LearningTypes';

export const SCHOOL_FRUGALITY_MODULE: LearningModule = {
	id: 'school-frugality',
	title: 'Frugality',
	description:
		'The lens that treats spending as a life cost — cutting waste and lifestyle creep to buy back margin, time, and options, without sliding into fear.',
	emoji: '🌿', // PLACEHOLDER — tune in presentation pass
	accentColor: '#2a9d8f', // from SchoolMeta color — tune in presentation pass
	tier: 'control',
	pillar: 'cash-flow-control',
	order: 0,
	surface: 'school',
	homeSchool: 'frugality',
	lessons: [
		{
			id: 'school-frugality-01',
			title: 'Frugality is not misery',
			description:
				'This lesson teaches the core frugality lens: spending less should create more choice, not a smaller life.',
			pillar: 'cash-flow-control',
			schoolsOfThought: ['frugality'],
			estimatedReadMinutes: 4,
			estimatedListenMinutes: 5,
			difficulty: 2,
			tags: ['conscious-restraint', 'life-cost', 'margin-creation'],
			prerequisites: [],
			relatedLessons: [],
			lessonKind: 'school-core',
			concepts: ['conscious restraint', 'life cost', 'margin creation'],
			blocks: [
				{
					type: 'paragraph',
					text: 'Frugality gets a bad reputation because people confuse it with being cheap. Cheap asks, how little can I spend? Frugality asks, what is this spending costing my life?',
				},
				{
					type: 'paragraph',
					text: 'Frugality is paying attention to the tradeoff behind every purchase. Money spent on things you barely care about becomes time, runway, and options you no longer have.',
				},
				{
					type: 'bullet_list',
					items: [
						'Cut what does not matter',
						'Protect what does matter',
						'Keep the gap that buys breathing room',
					],
				},
				{
					type: 'callout',
					text: 'The common mistake is treating frugality like punishment instead of power.',
				},
				{
					type: 'example',
					text: 'Skipping a random $18 lunch you barely enjoy is different from skipping dinner with a close friend. Frugality should know the difference.',
				},
				{
					type: 'paragraph',
					text: 'The blind spot is that cutting can become fear. If every dollar feels dangerous, frugality stops creating freedom and starts shrinking your life.',
				},
				{
					type: 'paragraph',
					text: 'Inside Nisk, this lens connects to your Dashboard and savings rate. You are not asking how to spend nothing. You are asking whether your spending matches the life you say you want.',
				},
				{
					type: 'key_takeaway',
					text: 'Frugality works when it cuts waste and protects life, not when it turns you against spending.',
				},
			],
			audioScript:
				'Frugality is one of those money ideas that gets misunderstood fast. People hear the word and picture someone counting pennies, refusing every invitation, buying the worst version of everything, and acting morally superior because they spent less. That is not the useful version.\n\nThe useful version of frugality is not misery. It is attention.\n\nFrugality looks at spending and asks a sharper question: what is this really costing me? Not only in dollars, but in time, pressure, runway, stress, and future options. If you spend money on something that genuinely improves your life, that may be worth it. If you spend money because you were bored, tired, influenced, pressured, or trying to feel better for ten minutes, the cost might be bigger than the receipt.\n\nThis is why frugality is not the same as cheapness. Cheapness only wants the lowest price. Frugality wants the best tradeoff. Cheapness might buy the worst shoes and replace them three times. Frugality might buy the better pair once because it fits the actual need. Cheapness might avoid every social plan. Frugality might skip the plans that do not matter so you can say yes to the ones that do.\n\nThe goal is not to hate spending. The goal is to stop letting random spending steal your margin.\n\nMargin matters because margin becomes breathing room. It can become debt payoff, emergency runway, investing, career flexibility, moving money, therapy money, family support, or just less panic. When every dollar is already assigned to habits you barely remember choosing, your life gets tighter.\n\nBut frugality has a blind spot. It can become fear. You can become so focused on spending less that every purchase feels like failure. You can start judging people. You can stop enjoying things that actually matter. You can avoid solving bigger problems, like income, housing, career, or health, because cutting tiny expenses feels more controllable.\n\nThat is when frugality turns small.\n\nThe stronger version is honest and flexible. It says, cut what does not matter so you can protect what does. Spend less where spending is automatic. Spend more carefully where spending actually supports your life. Keep a gap between income and expenses because that gap is what gives your future self more room.\n\nInside Nisk, the question is not, how do I become the cheapest possible person? The question is, does my spending match my values and my freedom path? Your Dashboard and savings rate can show whether your current choices are creating margin or quietly eating it.\n\nFrugality is not about making life gray. It is about refusing to trade your future options for things you barely wanted in the first place.',
			flashcards: [
				{
					id: 'school-frugality-01-fc-1',
					role: 'main_idea',
					front: 'What is frugality really?',
					back: 'Spending with attention so money creates more choice.',
				},
				{
					id: 'school-frugality-01-fc-2',
					role: 'watch_out',
					front: 'What is cheapness?',
					back: 'Chasing the lowest cost even when it makes life worse.',
				},
				{
					id: 'school-frugality-01-fc-3',
					role: 'use_it',
					front: 'How do you use frugality?',
					back: 'Cut what matters less so you can protect what matters more.',
				},
			],
			applyAction: {
				type: 'analyzeSavingsRate',
				label: 'Check your savings rate',
			},
			revision: 1,
		},
		{
			id: 'school-frugality-02',
			title: 'Small leaks become a lifestyle',
			description:
				'This lesson teaches how frugality notices repeated spending habits before they become permanent pressure.',
			pillar: 'cash-flow-control',
			schoolsOfThought: ['frugality'],
			estimatedReadMinutes: 4,
			estimatedListenMinutes: 5,
			difficulty: 2,
			tags: ['spending-leaks', 'lifestyle-creep', 'repeated-habits'],
			prerequisites: [],
			relatedLessons: [],
			lessonKind: 'school-core',
			concepts: ['spending leaks', 'lifestyle creep', 'repeated habits'],
			blocks: [
				{
					type: 'paragraph',
					text: 'One random purchase usually does not ruin anything. Frugality is more interested in the purchases that repeat until they become your normal life.',
				},
				{
					type: 'paragraph',
					text: 'This lens notices leaks. Food delivery, subscriptions, upgrades, fees, convenience, impulse buys, and social spending can slowly turn income into pressure.',
				},
				{
					type: 'bullet_list',
					items: [
						'Repeated spending becomes fixed expectation',
						'Convenience can become dependence',
						'Raises disappear when habits grow first',
					],
				},
				{
					type: 'callout',
					text: 'The common mistake is judging spending one purchase at a time while ignoring the pattern.',
				},
				{
					type: 'example',
					text: 'A $12 habit does not sound serious. If it repeats twenty times a month, it becomes $240 of missing margin before you even count the other leaks.',
				},
				{
					type: 'paragraph',
					text: 'The tradeoff is that some convenience is worth it. If a purchase saves real time, protects health, or lowers stress during a hard season, the answer is not automatically to cut it.',
				},
				{
					type: 'paragraph',
					text: 'Inside Nisk, this lens belongs in Simulate. You can test what happens when one leak shrinks and the gap gets stronger.',
				},
				{
					type: 'key_takeaway',
					text: 'Frugality watches patterns because repeated spending becomes the life you have to fund.',
				},
			],
			audioScript:
				'Frugality is not obsessed with one coffee, one lunch, or one subscription. That is the stereotype. The useful frugality lens is not standing over your shoulder yelling at every purchase. It is looking for patterns.\n\nOne purchase rarely defines your life. Repeated purchases do.\n\nThat is why spending leaks matter. A leak is not always dramatic. It is the thing that quietly repeats. Delivery because you are tired. Subscriptions you forgot about. Upgrades you did not really need. Convenience that became automatic. Fees you stopped noticing. Social spending you agreed to because saying no felt awkward. Random purchases that were not planned, not meaningful, and not remembered.\n\nIndividually, each one can look harmless. Together, they can become your lifestyle.\n\nThis is where frugality sees something important. A lifestyle is not built only by big decisions. It is built by repeated defaults. If your default is always the convenient option, the upgraded option, the rushed option, or the emotionally soothing option, your income starts needing to support that pattern. Then a raise comes in and somehow life still feels tight. Not because you made one huge mistake, but because the leaks grew with you.\n\nA $12 habit can sound small. But if it happens twenty times in a month, that is $240. Add two subscriptions, extra delivery fees, impulse shopping, and a few convenience purchases, and suddenly the gap between income and spending is weaker than it looked. That missing gap could have been breathing room. It could have been runway. It could have been debt progress. It could have been money toward something you actually care about.\n\nBut frugality should not become robotic. Some convenience is worth buying. If you are exhausted, caring for family, working long hours, dealing with health problems, or going through a hard season, convenience may be what keeps life stable. The point is not to cut everything. The point is to know which spending is helping and which spending is just leaking.\n\nA strong frugality habit asks: would I choose this again on purpose? Does this make my life better enough to justify the cost? Is this a real need, a real joy, or just a default? What pattern is this creating?\n\nInside Nisk, this belongs in simulation. You can test what happens if one leak gets smaller. What changes if $100 a month stops disappearing? What about $250? The point is not that every number must be cut. The point is to see the tradeoff.\n\nSmall leaks matter because they become the lifestyle your future self has to fund.',
			flashcards: [
				{
					id: 'school-frugality-02-fc-1',
					role: 'main_idea',
					front: 'Why do small leaks matter?',
					back: 'Repeated spending becomes a lifestyle and weakens your margin.',
				},
				{
					id: 'school-frugality-02-fc-2',
					role: 'watch_out',
					front: 'What is the pattern trap?',
					back: 'Judging one purchase while ignoring the repeated habit.',
				},
				{
					id: 'school-frugality-02-fc-3',
					role: 'use_it',
					front: 'What should you test?',
					back: 'Test what changes if one recurring leak shrinks.',
				},
			],
			applyAction: {
				type: 'openSimulator',
				label: 'Simulate lower spending',
				scenarioType: 'expense-slash',
			},
			revision: 1,
		},
		{
			id: 'school-frugality-03',
			title: 'Where frugality gets too small',
			description:
				'This lesson teaches the limits of frugality and why cutting costs should not become fear, shame, or avoidance.',
			pillar: 'cash-flow-control',
			schoolsOfThought: ['frugality'],
			estimatedReadMinutes: 5,
			estimatedListenMinutes: 6,
			difficulty: 3,
			tags: ['frugality-limits', 'scarcity-fear', 'bigger-levers'],
			prerequisites: [],
			relatedLessons: [],
			lessonKind: 'school-core',
			concepts: ['frugality limits', 'scarcity fear', 'bigger levers'],
			blocks: [
				{
					type: 'paragraph',
					text: 'Frugality is powerful, but it has limits. You can only cut so much before the problem is no longer waste. Sometimes the bigger issue is income, rent, debt, health, or family obligation.',
				},
				{
					type: 'paragraph',
					text: 'This lens breaks down when it treats every money problem like a spending problem. Some people do not need more guilt. They need more margin, more income, more stability, or fewer fixed pressures.',
				},
				{
					type: 'bullet_list',
					items: [
						'Cutting has a floor',
						'Income has room to grow',
						'Some costs are not optional',
					],
				},
				{
					type: 'callout',
					text: 'The common mistake is using frugality to avoid bigger decisions.',
				},
				{
					type: 'example',
					text: 'Canceling a few subscriptions can help. But if rent takes half your income, the real decision may be housing, career, roommates, location, or a longer plan.',
				},
				{
					type: 'paragraph',
					text: 'The blind spot is emotional too. Frugality can become scarcity fear, where spending on anything starts to feel unsafe.',
				},
				{
					type: 'paragraph',
					text: 'Inside Nisk, this lens works best as one part of the picture. Use it to find waste, then ask whether the bigger lever is spending, earning, runway, or a life redesign.',
				},
				{
					type: 'key_takeaway',
					text: 'Frugality is a strong lever, but it should not become the only lever.',
				},
			],
			audioScript:
				'Frugality is useful, but it is not a complete answer to every money problem. This is where the lens needs humility.\n\nSome people talk about frugality like every financial struggle comes from buying too much coffee, eating out too often, or failing to be disciplined. That is too simple. Spending matters, yes. Habits matter, yes. But rent matters too. Income matters. Debt matters. Healthcare matters. Transportation matters. Childcare matters. Family support matters. Location matters. Luck matters.\n\nIf someone is wasting money on things they do not care about, frugality can help fast. It can create margin. It can reduce pressure. It can make the budget feel less chaotic. It can help someone stop bleeding money from habits they never chose on purpose.\n\nBut if someone already lives tightly, cutting more may not be the answer. There is a floor. You cannot cut rent to zero. You cannot always cut medical costs, family obligations, or basic transportation. You cannot meal prep your way out of every structural problem. At some point, the bigger lever may be income, career, housing, debt strategy, relocation, or support.\n\nThat is why frugality can get too small. It can keep you focused on tiny decisions because tiny decisions feel controllable. Cancel a subscription. Buy cheaper soap. Skip a lunch. Those things may help, but they may also distract from the harder question: is my whole life setup too tight?\n\nThere is also an emotional risk. Frugality can turn into scarcity fear. You start feeling guilty for every purchase. You avoid spending on health, tools, relationships, or experiences that genuinely matter. You become proud of spending less even when spending a little more would protect your time, energy, or peace. That is not freedom. That is fear with a budget.\n\nThe better version of frugality is part of a larger system. It finds waste. It protects margin. It challenges lifestyle creep. Then it asks what other levers matter. Can income grow? Can fixed costs change over time? Can runway reduce pressure? Can your life design shift? Can one big decision matter more than twenty tiny cuts?\n\nInside Nisk, this lens should not trap the user in guilt. It should help them see clearly. Maybe spending is the lever. Maybe earning is. Maybe runway is. Maybe the real issue is a fixed cost that needs a longer plan.\n\nFrugality is powerful when it creates options. It gets too small when it becomes the only answer.',
			flashcards: [
				{
					id: 'school-frugality-03-fc-1',
					role: 'main_idea',
					front: 'Where does frugality break?',
					back: 'When it treats every money problem as a spending problem.',
				},
				{
					id: 'school-frugality-03-fc-2',
					role: 'watch_out',
					front: 'What is scarcity fear?',
					back: 'Feeling unsafe or guilty about spending even when it supports life.',
				},
				{
					id: 'school-frugality-03-fc-3',
					role: 'use_it',
					front: 'What bigger lever matters?',
					back: 'Ask whether spending, income, runway, or life design is the real constraint.',
				},
			],
			applyAction: {
				type: 'noToolAction',
				label: 'Find the bigger lever',
				prompt: 'Write whether your biggest money pressure is spending, income, fixed costs, debt, runway, or life design.',
			},
			revision: 1,
		},
	],
};
